import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
} from "@mui/material";
import {
  FileUp,
  CloudUpload,
  CheckCircle,
  X,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";

import { uploadCSVData } from "../utils/requests";
import { getBatchData } from "../utils/aqi";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  textAlign: "center",
};

const UploadCsvModal: React.FC = () => {
  const [mlVariant, setMlVariant] = useState<string>("ml1");
  const [open, setOpen] = useState(false);
  const [jsonData, setJsonData] = useState<object[] | []>([]);

  const [isUploading, setIsUploading] = useState<Boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [completedBatches, setCompletedBatches] = useState<number[]>([]);
  const [failedBatches, setFailedBatches] = useState<number[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentUploadState, setCurrentUploadState] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  let batchSize = totalRecords >= 1000 ? 1000 : totalRecords >= 100 ? 100 : 10;

  const handleClose = () => {
    setOpen(false);
    setIsUploading(false);
    setUploadProgress(0);
    setJsonData([]);
    setCompletedBatches([]);
    setFileName("");
  };

  useEffect(() => {
    setTotalRecords(jsonData?.length - 1 || 0);
  }, [jsonData]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "text/csv") {
      const reader = new FileReader();

      reader.onload = (e) => {
        const text = e.target?.result as string;
        const rows = text.split("\n").filter((row) => row.trim());
        const headers = rows[0].split(",").map((h) => h.trim());

        let hasError = false;
        const errorRows: number[] = [];

        const data = rows
          .slice(1)
          .map((row, rowIndex) => {
            const values = row.split(",").map((v) => v.trim());
            const record = headers.reduce((acc, header, index) => {
              acc[header] = values[index] ?? "";
              return acc;
            }, {} as Record<string, string>);

            // Check for missing values
            const isMissing = Object.values(record).some(
              (value) => value === ""
            );
            if (isMissing) {
              hasError = true;
              errorRows.push(rowIndex + 2); // +2 to account for header + 1-based index
              return null;
            }

            return record;
          })
          .filter((record) => record !== null);

        if (hasError) {
          setErrorDialogMessage(
            `Missing values found in row(s): ${errorRows.join(
              ", "
            )}. Please check your CSV.`
          );
          setErrorDialogOpen(true);
          handleClose();
          return;
        }

        setFileName(file.name);
        setJsonData(data); // All good
      };

      reader.readAsText(file);
    } else {
      setErrorDialogMessage("Please upload a valid CSV file.");
      setErrorDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    setIsUploading(true);
    setUploadProgress(0);
    setCompletedBatches([]);

    let batch = 1;
    const totalBatches = Math.ceil(totalRecords / batchSize);
    const maxRetries = 3;

    while (batch <= totalBatches) {
      let success = false;
      let attempts = 0;

      const start = (batch - 1) * batchSize;
      const end = Math.min(start + batchSize - 1, totalRecords);

      while (!success && attempts < maxRetries) {
        try {
          setCurrentUploadState(`Batch ${batch} uploading ...`);

          const response = await uploadCSVData({
            range: `${start} to ${end}`,
            data: getBatchData(jsonData, start, end),
          });

          if (response === 200) {
            setCompletedBatches((prev) => [...prev, batch - 1]);
            setUploadProgress(batch);
            setCurrentUploadState(`Batch ${batch} uploaded successfully.`);
            success = true;

            await new Promise((resolve) => setTimeout(resolve, 500));
          } else {
            throw new Error(
              `Batch ${batch} failed with status ${response.status}`
            );
          }
        } catch (error) {
          attempts++;
          setCurrentUploadState(
            `Attempt ${attempts} failed for batch ${batch}`
          );
          console.error(
            `Attempt ${attempts} failed for batch ${batch}:`,
            error
          );

          if (attempts >= maxRetries) {
            setCurrentUploadState(
              `Batch ${batch} failed after ${maxRetries} attempts.`
            );
          }
        }
      }

      batch++;
      setFileName("");
    }

    setUploadProgress(0);
    setCurrentUploadState("All batches processed.");
  };

  return (
    <>
      <Tooltip
        title="Upload CSV File"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -14],
                },
              },
            ],
          },
        }}
      >
        <IconButton onClick={() => setOpen(true)} sx={{ mr: "1vw" }}>
          <FileUp color="white" size={30} />
        </IconButton>
      </Tooltip>
      <Modal open={open}>
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: "-21vh",
            }}
          >
            <IconButton onClick={() => handleClose()} size="small">
              <X />
            </IconButton>
          </Box>
          {isUploading ? (
            <Box>
              <Typography variant="h6">Uploading Data</Typography>
              <Divider sx={{ my: 2 }} />
              <Accordion>
                <AccordionSummary
                  expandIcon={<ChevronDown />}
                  id="panel1a-header"
                >
                  <Typography>{currentUploadState}</Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                >
                  <List>
                    {Array.from(
                      { length: Math.ceil(totalRecords / batchSize) },
                      (_, i) => {
                        const start = i * batchSize + 1;
                        const end = Math.min(
                          start + batchSize - 1,
                          totalRecords
                        );

                        return (
                          <ListItem key={i}>
                            <ListItemText
                              primary={`Uploading ${start}-${end}`}
                            />
                            <ListItemIcon>
                              {completedBatches.includes(i) ? (
                                <CheckCircle color="green" size={24} />
                              ) : uploadProgress === i ? (
                                <CircularProgress size={24} />
                              ) : null}
                            </ListItemIcon>
                          </ListItem>
                        );
                      }
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" component="h2" gutterBottom>
                Upload CSV File
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <FormControl variant="standard" sx={{ width: 120, mt: 2 }}>
                  <InputLabel>ML Calibrated</InputLabel>
                  <Select
                    value={mlVariant}
                    onChange={(e) => setMlVariant(e.target.value)}
                    label="ML Calibrated"
                    disabled
                  >
                    <MenuItem value="ml1">ML1</MenuItem>
                    <MenuItem value="ml2">ML2</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  sx={{ mt: 2 }}
                  onClick={() =>
                    document?.getElementById("file-upload")?.click()
                  }
                >
                  <CloudUpload size={60} />
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={handleFileUpload}
                  />
                </IconButton>
                <Typography variant="body1">{fileName}</Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 1,
                    color: "warning.main",
                  }}
                >
                  <AlertTriangle size={50} style={{ marginRight: 6 }} />
                  <Typography variant="caption" sx={{ color: "inherit" }}>
                    Any row with blank values for FM,TempC, RH, or cf1
                    columns will be rejected. Please recheck before uploading.
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="contained" onClick={handleUpload}>
                  Upload
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Error Dialog */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            File Error
          </Typography>
          <Typography variant="body1">{errorDialogMessage}</Typography>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => setErrorDialogOpen(false)}
              variant="contained"
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default UploadCsvModal;
